import { Alert, Form, Input, Button, Checkbox } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { getFriendlyErrorMessage } from '../../../lib/notification';
import type { LoginRequest } from '../../../types/auth.types';

interface LoginFormValues extends LoginRequest {
	remember?: boolean;
}

/**
 * Login Form Component
 *
 * Features:
 * - Email and password validation
 * - Remember me checkbox
 * - Error handling from API
 * - Integration with useAuth hook
 */
export function LoginForm() {
	const [form] = Form.useForm<LoginFormValues>();
	const { login, isLoggingIn, loginError, resetLoginError } = useAuth();

	const handleSubmit = async (values: LoginFormValues) => {
		const { email, password, remember } = values;

		// Store remember preference
		if (remember) {
			localStorage.setItem('remember_me', 'true');
		} else {
			localStorage.removeItem('remember_me');
		}

		resetLoginError();
		await login({ email, password }).catch(() => {});
	};

	return (
		<div className="login-form-container">
			{/* Header */}
			<div className="login-form-header">
				<h1 className="login-form-title">Welcome back</h1>
				<p className="login-form-subtitle">Sign in to your account to continue</p>
			</div>

			{loginError && (
				<Alert
					type="error"
					message={getFriendlyErrorMessage(loginError, 'Invalid email or password')}
					closable
					onClose={resetLoginError}
					style={{ marginBottom: 16 }}
				/>
			)}

			{/* Form */}
			<Form
				form={form}
				name="login"
				layout="vertical"
				onFinish={handleSubmit}
				autoComplete="off"
				size="large"
				initialValues={{
					remember: localStorage.getItem('remember_me') === 'true',
				}}
			>
				{/* Email Field */}
				<Form.Item
					name="email"
					label="Email"
					rules={[
						{ required: true, message: 'Please enter your email' },
						{ type: 'email', message: 'Please enter a valid email' },
					]}
				>
					<Input
						prefix={<UserOutlined className="login-input-icon" />}
						placeholder="name@company.com"
						autoComplete="email"
					/>
				</Form.Item>

				{/* Password Field */}
				<Form.Item
					name="password"
					label="Password"
					rules={[{ required: true, message: 'Please enter your password' }]}
				>
					<Input.Password
						prefix={<LockOutlined className="login-input-icon" />}
						placeholder="Enter your password"
						autoComplete="current-password"
					/>
				</Form.Item>

				{/* Remember Me & Forgot Password */}
				<div className="login-form-options">
					<Form.Item name="remember" valuePropName="checked" noStyle>
						<Checkbox>Remember me</Checkbox>
					</Form.Item>
					<a href="#" className="login-forgot-link">
						Forgot password?
					</a>
				</div>

				{/* Submit Button */}
				<Form.Item>
					<Button
						type="primary"
						htmlType="submit"
						loading={isLoggingIn}
						block
						className="login-submit-button"
					>
						{isLoggingIn ? 'Signing in...' : 'Sign in'}
					</Button>
				</Form.Item>
			</Form>

			{/* Demo Credentials Hint (for development) */}
			{import.meta.env.DEV && (
				<div className="login-demo-hint">
					<p className="login-demo-text">
						Demo credentials: <strong>demo@example.com</strong> / <strong>demo123</strong>
					</p>
					<Button
						type="link"
						size="small"
						onClick={() => {
							form.setFieldsValue({
								email: 'demo@example.com',
								password: 'demo123',
							});
						}}
					>
						Use demo credentials
					</Button>
				</div>
			)}
		</div>
	);
}
